import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import {
  buildUserAvatarUrl,
  subscribeToAvatarChanges,
} from "../../../lib/userAvatar";

const fieldSx = {
  "& .MuiInputLabel-root": {
    color: "rgb(30, 41, 59)",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
    position: "relative",
    transform: "none",
    mb: 0.75,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    minHeight: 48,
  },
  "& .MuiOutlinedInput-input": {
    fontSize: 14,
    lineHeight: "20px",
    px: 2,
    py: 1.75,
  },
  "& legend": {
    display: "none",
  },
  "& fieldset": {
    top: 0,
  },
};

export default function UserProfileDialog({
  onClose,
  onSaveProfile,
  open,
  profileSaving = false,
  user,
}) {
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [form, setForm] = useState({
    email: "",
    name: "",
    nickname: "",
  });
  const avatarUrl = buildUserAvatarUrl(user);
  const initial = (user?.name || user?.username || user?.email || "Z").trim().charAt(0).toUpperCase();

  useEffect(() => subscribeToAvatarChanges(() => {
    setAvatarVersion((current) => current + 1);
  }), []);

  useEffect(() => {
    if (!open) return;

    setForm({
      email: user?.email || "",
      name: user?.name || user?.username || "",
      nickname: user?.nickname || "",
    });
  }, [open, user]);

  function updateField(name) {
    return (event) => {
      setForm((current) => ({
        ...current,
        [name]: event.target.value,
      }));
    };
  }

  async function handleSave() {
    await onSaveProfile?.({
      name: form.name,
      nickname: form.nickname,
    });
    onClose?.();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow:
            "0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
          maxHeight: "calc(100vh - 32px)",
          overflow: "hidden",
          width: { xs: "calc(100% - 32px)", sm: 448 },
        },
      }}
    >
      <Box
        sx={{
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          px: 3,
          py: 3,
        }}
      >
        <Typography
          sx={{
            color: "rgb(30, 41, 59)",
            fontSize: 16,
            fontWeight: 500,
            lineHeight: "24px",
            mb: 1.25,
          }}
        >
          Editar meus dados
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
            position: "relative",
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              bgcolor: "rgb(226, 232, 240)",
              borderRadius: "50%",
              color: "rgb(71, 85, 105)",
              display: "flex",
              fontSize: 58,
              fontWeight: 700,
              height: 128,
              justifyContent: "center",
              overflow: "hidden",
              width: 128,
            }}
          >
            {avatarUrl ? (
              <Box
                key={avatarVersion}
                component="img"
                src={avatarUrl}
                alt=""
                sx={{ height: "100%", objectFit: "cover", width: "100%" }}
              />
            ) : (
              initial
            )}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gap: 2.5 }}>
          <TextField
            fullWidth
            label="Nome *"
            value={form.name}
            onChange={updateField("name")}
            sx={fieldSx}
          />
          <TextField
            fullWidth
            label="Apelido"
            value={form.nickname}
            onChange={updateField("nickname")}
            sx={fieldSx}
          />
          <TextField
            fullWidth
            label="E-mail"
            value={form.email}
            onChange={updateField("email")}
            InputProps={{ readOnly: true }}
            sx={fieldSx}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            mt: 4,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={profileSaving}
            sx={{
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 500,
              height: 40,
              minWidth: 88,
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={profileSaving}
            sx={{
              borderRadius: 999,
              boxShadow: "none",
              fontSize: 13,
              fontWeight: 600,
              height: 40,
              minWidth: 88,
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            {profileSaving ? "Salvando..." : "Salvar"}
          </Button>
        </Box>
      </Box>

    </Dialog>
  );
}
